import {
  Navbar,
  NavBody,
  NavItems,
  MobileNav,
  NavbarLogo,
  NavbarButton,
  MobileNavHeader,
  MobileNavToggle,
  MobileNavMenu,
} from "@/components/ui/resizable-navbar";
import { MobileMenuContent } from "@/components/MobileMenuContent";
import { useState } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import logoWhite from "@/assets/logo-white.svg";
import logoBlack from "@/assets/logo-black.svg";
import Footer from "@/components/Footer";
import { useWaitlistModal } from "@/components/waitlist/WaitlistContext";
import SecondaryNav from "@/components/SecondaryNav";
import BusinessUnitsSection from "@/components/about/BusinessUnitsSection";
import MaskedVideoHero from "@/components/about/MaskedVideoHero";
import PlatformSection from "@/components/about/PlatformSection";
import VisionSection from "@/components/about/VisionSection";
import PeopleSection from "@/components/about/PeopleSection";
import GovernanceSection from "@/components/about/GovernanceSection";
import { CtaFooter, DynamicCtaFooter, PageSeo } from "@/components/ds";
import ctaFooterBg from "@/assets/about-cta-bg.jpg";
import { usePrerenderReadyImmediate } from "@/hooks/usePrerenderReady";
import { useScrollDepth } from "@/hooks/useScrollDepth";
import { SITE_URL, buildUrl } from "@/config/site";
import { usePeopleProfiles } from "@/hooks/usePeopleProfiles";

const NavButtons = ({ visible, onWaitlistClick }: { visible?: boolean; onWaitlistClick: (triggerElement?: string) => void }) => (
  <div className="flex items-center gap-8">
    <NavbarButton href="/contact" variant="secondary" visible={visible}>Contact</NavbarButton>
    <NavbarButton variant="primary" visible={visible} onClick={() => onWaitlistClick('navbar_cta')}>Join Waitlist</NavbarButton>
  </div>
);
export default function About() {
  useScrollDepth();

  const navItems = [
    { name: "Invest", link: "/invest" },
    { name: "Learn", link: "/learn" },
    { name: "About", link: "/about" },
  ];

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showSecondaryNav, setShowSecondaryNav] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { openModal } = useWaitlistModal();

  // Signal to Prerender.io that the page is ready (mostly static content)
  usePrerenderReadyImmediate();
  const { data: peopleProfiles } = usePeopleProfiles();
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const scrollDelta = latest - lastScrollY;
    
    // Always show when at the top of the page
    if (latest <= 50) {
      setShowSecondaryNav(true);
    }
    // Only trigger hide after scrolling down 50px+
    else if (scrollDelta > 10 && latest > 100) {
      setShowSecondaryNav(false);
    }
    // Only trigger show after scrolling up 30px+
    else if (scrollDelta < -30) {
      setShowSecondaryNav(true);
    }
    
    // Only update lastScrollY if we've moved significantly (reduces jitter)
    if (Math.abs(scrollDelta) > 5) {
      setLastScrollY(latest);
    }
  });

  const secondaryNavItems = [
    { label: "Vision", anchor: "vision" },
    { label: "Platform", anchor: "platform" },
    { label: "Company", anchor: "operating-businesses" },
    { label: "People", anchor: "people" },
    { label: "Governance", anchor: "governance" },
  ];

  return (
    <div className="relative w-full min-h-screen bg-background">
      <PageSeo pageSlug="/about">
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "AboutPage",
                "@id": buildUrl('/about'),
                "url": buildUrl('/about'),
                "name": "About Fedgroup Private Markets | 30+ Years of Investment Expertise",
                "description": "Fedgroup Private Markets brings 30+ years of institutional investment expertise to individual investors. Learn about our vision, platform, team, and governance.",
                "isPartOf": { "@type": "WebSite", "@id": `${SITE_URL}/#website` },
                "mainEntity": { "@id": `${SITE_URL}/#organization` }
              },
              
              ...(peopleProfiles ? peopleProfiles.filter(p => p.is_active).map(p => {
                const person: Record<string, any> = {
                  "@type": "Person",
                  "@id": `${buildUrl('/about')}#person-${p.name.toLowerCase().replace(/\s+/g, '-')}`,
                  "name": p.name,
                  "jobTitle": p.title,
                  "worksFor": { "@type": "Organization", "@id": `${SITE_URL}/#organization` },
                };
                if (p.bio) person.description = p.bio;
                if (p.image_url) person.image = p.image_url;
                person.url = buildUrl('/about');
                if (p.linkedin_url) person.sameAs = [p.linkedin_url];
                if (p.specialisations.length > 0) {
                  person.knowsAbout = p.specialisations.map(s => s.specialisation);
                }
                if (p.credentials.length > 0) {
                  person.hasCredential = p.credentials.map(c => ({
                    "@type": "EducationalOccupationalCredential",
                    "credentialCategory": "Professional Certification",
                    "name": c.credential,
                  }));
                }
                if (p.education.length > 0) {
                  person.alumniOf = p.education.map(e => {
                    const parts = e.education.split(' | ');
                    if (parts.length >= 2) {
                      return {
                        "@type": "EducationalOrganization",
                        "name": parts[1].trim(),
                        "description": parts[0].trim(),
                      };
                    }
                    return {
                      "@type": "EducationalOrganization",
                      "name": e.education,
                    };
                  });
                }
                return person;
              }) : []),
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": buildUrl('/') },
                  { "@type": "ListItem", "position": 2, "name": "About", "item": buildUrl('/about') }
                ]
              }
            ]
          })}
        </script>
      </PageSeo>
      <Navbar forceVisible mobileMenuOpen={isMobileMenuOpen}>
        <NavBody>
          <NavbarLogo logoWhite={logoWhite} logoBlack={logoBlack} />
          <NavItems items={navItems} />
          <NavButtons onWaitlistClick={openModal} />
        </NavBody>

        <MobileNav>
          <MobileNavHeader>
            <NavbarLogo logoWhite={logoWhite} logoBlack={logoBlack} />
            <MobileNavToggle
              isOpen={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            />
          </MobileNavHeader>

          <MobileNavMenu
            isOpen={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
          >
            <MobileMenuContent
              navItems={navItems}
              onItemClick={() => setIsMobileMenuOpen(false)}
              onWaitlistClick={openModal}
              secondaryNavItems={secondaryNavItems}
            />
          </MobileNavMenu>
        </MobileNav>
      </Navbar>

      {/* Secondary Navigation */}
      <motion.div
        initial={{ y: 0 }}
        animate={{ y: showSecondaryNav ? 0 : -100 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed top-[44px] left-0 right-0 z-30 hidden lg:block"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "saturate(180%) blur(20px)",
        }}
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3 flex justify-center">
          <SecondaryNav items={secondaryNavItems} visible={true} />
        </div>
      </motion.div>

      {/* Masked Video Hero */}
      <MaskedVideoHero />

      {/* Vision Section */}
      <VisionSection />

      {/* Platform Section */}
      <PlatformSection />

      {/* Business Units Section */}
      <BusinessUnitsSection />

      {/* People Section */}
      <PeopleSection />

      {/* Governance Section */}
      <GovernanceSection />

      {/* component-cta-footer: Dynamic CMS or fallback */}
      <DynamicCtaFooter
        pageSlug="/about"
        fallback={
          <CtaFooter
            headline="Secure early access to&#10;Fedgroup Private Markets"
            buttonLabel="Join waitlist"
            backgroundImage={ctaFooterBg}
          />
        }
      />

      <Footer />
    </div>
  );
}
